/**
 * PersonelFirmaBelgeParcalari.jsx — kişi satırında FİRMA belge gruplarının kareleri.
 *
 * Backlog #124 (backend #123 / #119). `GET /personnel/kisi-listesi?belgeler=true` her
 * `firmalar[]` girdisine O FİRMANIN iki grubunu koyar:
 *   `belgeGruplari: [{ kod: 'FIRMA'|'ISG', ad, status, tamam, belgeler: [...] }]`
 * (şekil ve tuzaklar: Components/Firma/belgeGruplari.js, util/knowledgebase/firma-belge-gruplari.md).
 *
 * ⚠️ Bir satır BİRDEN ÇOK firmayı kapsayabilir ve iki grup firma başınadır. Satırın karesi hangi
 * firmayı konuşuyor sorusunun tek cevabı `onFirmaSec` (en yeşil firma, #399): vesikalık çerçevesi (#126) ve
 * "Kart Yazdır" (#128) da AYNI firmayı kullanır, yoksa kareler bir firmayı, çerçeve başka birini gösterir.
 * Öndekinin dışındakiler "+N" rozetinin arkasında (popover).
 *
 * Grup rengi firma panelindeki Evraklar sekmesiyle aynı mantıktan gelir (`grupDurumInfo`): eksik ya
 * da reddedilmiş kırmızı, onay bekleyen sarı, tamam yeşil; hiç kayıt yoksa nötr gri. Grup `kod`
 * ile bulunur, `ad` ile değil. Kareler `href` verilirse (Kişi Listesi, #273) o firmanın Belgeler sayfasını
 * yeni sekmede açar; verilmezse salt görsel.
 */
import React from "react";
import { Button, UncontrolledPopover, PopoverBody } from "reactstrap";
import { Building2, ClipboardCheck } from "lucide-react";
import { StatusTile, STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import { kartKarariUc, KART_KARARI_UC_SIRASI } from "../../services/kartDurumuService";
import { siraliBelgeGruplari, grupDurumInfo, grupKartNotu, belgeGorunenAd, belgeUygulanamazMi, uygulanamazDurumu } from "../Firma/belgeGruplari";
import { useFirmaRedNedenleri } from "../Firma/firmaRedNedenleri";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const GRUP_IKON = { FIRMA: Building2, ISG: ClipboardCheck };

/** `firmaName` null olabilir (silinmiş firma) — id'yle göster, girdiyi düşürme. */
export function firmaAdi(firma) {
  return firma?.firmaName || (firma?.firmaId ? `Firma #${firma.firmaId}` : "—");
}

// Kart kararı sırası (küçük = daha yeşil): backend'in `kararSirasi`yla aynı — yeşil sarıyı, sarı kırmızıyı yener; yeşillerde
// TAM > YETERLI > OZEL_IZIN. Karar yoksa (Tüm kişiler: karar hesaplanmaz) hepsi eşit sayılır.
const KARAR_SIRASI = { KART_ALABILIR: 0, EVRAK_TAMAM_KART_ALAMAZ: 1, EVRAK_EKSIK: 2 };
const SEVIYE_SIRASI = { TAM: 0, YETERLI: 1, OZEL_IZIN: 2 };

/** Bir firmanın `belgeGruplari`ndaki doluluk: tamam grup sayısı, onaylı belge sayısı, onay bekleyen belge sayısı. */
function firmaDolulugu(firma) {
  let tamam = 0;
  let onayli = 0;
  let bekleyen = 0;
  (firma?.belgeGruplari || []).forEach((g) => {
    if (g.tamam === true || g.status === 1) tamam += 1;
    (g.belgeler || []).forEach((b) => {
      if (b.zorunlu === false) return; // grupta sayılmayan belge (ör. Fuar Sözleşmesi) doluluğu oynatmasın
      if (b.status === 1) onayli += 1;
      else if (b.status === 0) bekleyen += 1;
    });
  });
  return { tamam, onayli, bekleyen };
}

/**
 * İki firma girdisini "daha yeşil önde" sırasına koyar: üç durumlu karar (#531: yeşil < alanda < kırmızı) → backend kararı → tamam grup →
 * onaylı belge → bekleyen belge. `ctx` satırın Özel İzni ve KKD'si: üç durumlu karar bunlarla türetilir (kartKarariUc).
 */
function firmaKarsilastir(a, b, ctx) {
  const uc = (f) => KART_KARARI_UC_SIRASI[kartKarariUc(f, ctx).key] ?? 3;
  const ucFark = uc(a) - uc(b);
  if (ucFark) return ucFark;
  const karar =(KARAR_SIRASI[a.kartDurumu] ?? 3) - (KARAR_SIRASI[b.kartDurumu] ?? 3);
  if (karar) return karar;
  const seviye = (SEVIYE_SIRASI[a.kartSeviyesi] ?? 3) - (SEVIYE_SIRASI[b.kartSeviyesi] ?? 3);
  if (seviye) return seviye;
  const da = firmaDolulugu(a);
  const db = firmaDolulugu(b);
  return (db.tamam - da.tamam) || (db.onayli - da.onayli) || (db.bekleyen - da.bekleyen);
}

/**
 * Satırın karelerinin (ve çerçevenin, "Kart Yazdır"ın) konuştuğu firma:
 *   1. Firma süzgeci açıksa o firma (karar da onunki),
 *   2. aksi halde EN YEŞİL firma (#399): kart kararı en iyi olan (fuar görünümü — her `firmalar[]` girdisi kendi kararını
 *      taşır), eşitlikte firma belgeleri en dolu olan (Tüm kişiler'de karar yok, yalnızca `belgeGruplari` var). Backend'in satır
 *      düzeyindeki `kartFirmaId`si eşitlikte firma adına göre ilk firmayı seçtiği için burada ona güvenilmez — iki firma da
 *      kırmızıyken evrakı yeşil olan firma gizli kalırdı,
 *   3. hepsi eşitse `firmalar[0]` — Tüm kişiler'de en son çalıştığı firma (backend #119).
 */
export function onFirmaSec(person, firmaFiltreId) {
  const firmalar = person?.firmalar || [];
  const hedef = Number(firmaFiltreId) || null;
  const filtreli = hedef && firmalar.find((f) => Number(f.firmaId) === hedef);
  if (filtreli) return filtreli;
  // Kararlı sıralama: eşitlikte backend sırası (firmalar[0] önde) korunur.
  const ctx = { ozelIzin: person?.singlePermit ?? null, kkd: person?.kkd ?? null };
  return firmalar.reduce((en, f) => (en && firmaKarsilastir(f, en, ctx) >= 0 ? en : f), null);
}

function belgeDurumYazisi(belge) {
  // #442: UYGULANAMAZ belge "Yok" okunmaz.
  if (belgeUygulanamazMi(belge)) return uygulanamazDurumu().label;
  if (belge.status != null) return FILE_APPROVAL_STATUS[belge.status]?.label ?? "—";
  return belge.fuaraOzel ? i18n.t("components:personelFirmaBelge.fuaraOzelBuGorunumdeYok") : "Yok";
}

function grupKareBilgisi(grup) {
  if (grup.status == null) {
    return { background: NEUTRAL_GRAY, durum: i18n.t("components:personelFirmaBelge.kayitYok") };
  }
  const info = grupDurumInfo(grup);
  return { background: STATUS_HEX[info.color] ?? NEUTRAL_GRAY, durum: info.label };
}

/**
 * Bir firmanın iki grup karesi (FIRMA, sonra ISG). Grup yoksa hiçbir şey basılmaz.
 * `href` verilirse iki kare de o firmanın Belgeler sayfasına YENİ SEKMEDE açılır (#273).
 * `etiketli` (#531): karenin altına "Firma" / "İSG" yazısı konur (Kişi Listesi'nin gruplu düzeni).
 */
export function FirmaGrupKareleri({ gruplar, href, etiketli = false }) {
  const { t } = useTranslation("components");
  const sirali = siraliBelgeGruplari(gruplar || []);
  // #737: the company's own list shows why a company document was rejected in the tooltip (empty outside its provider, i.e. for staff).
  const redNedenleri = useFirmaRedNedenleri(gruplar);
  return (
    <>
      {sirali.map((grup) => {
        const { background, durum } = grupKareBilgisi(grup);
        // WAS (until #737): `• ${belgeGorunenAd(b.fileTypeId, b.ad)}: ${belgeDurumYazisi(b)}${b.zorunlu === false ? ... : ""}` - no reason.
        const satirlar = (grup.belgeler || []).map((b) => {
          const neden = b.status === 2 ? redNedenleri[b.fileTypeId] : null;
          return `• ${belgeGorunenAd(b.fileTypeId, b.ad)}: ${belgeDurumYazisi(b)}${neden ? ` — ${i18n.t("portal:firmaKart.redNedeni")} ${neden}` : ""}${b.zorunlu === false ? t("personelFirmaBelge.grubaSayilmaz") : ""}`;
        });
        return (
          <StatusTile
            key={grup.kod}
            icon={GRUP_IKON[grup.kod] ?? Building2}
            background={background}
            label={[`${grup.baslik} — ${durum}${grupKartNotu(grup) ? ` (${grupKartNotu(grup)})` : ""}`, ...satirlar].join("\n")}
            href={href}
            caption={etiketli ? t(grup.kod === "ISG" ? "personelKart.isg" : "personelKart.etiketFirma") : undefined}
          />
        );
      })}
    </>
  );
}

/**
 * Öndeki firmanın dışındaki firmalar: "+N" düğmesi, tıklayınca her birinin iki karesi.
 * `hrefOf(firma)` verilirse her firmanın kareleri KENDİ firmasının Belgeler sayfasına bağlanır.
 */
export function DigerFirmalarRozeti({ personId, firmalar, hrefOf }) {
  const { t } = useTranslation("components");
  if (!firmalar?.length) return null;
  const id = `diger-firma-${personId}`;
  return (
    <>
      <Button
        id={id} type="button" color="light" size="sm" className="border text-nowrap"
        title={t("personelFirmaBelge.digerFirma", { length: firmalar.length })}
      >
        +{firmalar.length}
      </Button>
      <UncontrolledPopover target={id} trigger="legacy" placement="bottom">
        <PopoverBody>
          <div className="vstack gap-2">
            {firmalar.map((f) => (
              <div key={f.firmaId} className="d-flex align-items-center gap-2">
                <div className="flex-grow-1 small text-truncate" style={{ minWidth: 120, maxWidth: 220 }} title={firmaAdi(f)}>
                  {firmaAdi(f)}
                </div>
                <FirmaGrupKareleri gruplar={f.belgeGruplari} href={hrefOf?.(f)} />
              </div>
            ))}
          </div>
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
}
